import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSubscription } from '../../context/SubscriptionContext';
import useSubscriptionApi from '../../hooks/useSubscriptionApi';
import useCountdownRedirect from '../../hooks/useCountdownRedirect';
import '../../styles/SubscriptionSuccess.css';

/**
 * SubscriptionSuccess Component
 *
 * Success page shown after completing subscription checkout.
 * Features:
 * - Auto-refreshes subscription status
 * - Shows subscription details
 * - Auto-redirects to dashboard after countdown
 */
const SubscriptionSuccess = () => {
    const { t } = useTranslation('subscription');
    const navigate = useNavigate();
    const location = useLocation();

    const {
        refreshSubscriptionStatus,
        subscriptionData,
        formatDate
    } = useSubscription();

    const { verifyCheckout } = useSubscriptionApi();

    const [isRefreshing, setIsRefreshing] = useState(true);

    // Countdown redirect hook
    const { countdown } = useCountdownRedirect('/dashboard', 10, !isRefreshing);

    // Verify checkout and refresh subscription on mount
    useEffect(() => {
        const sessionId = new URLSearchParams(location.search).get('session_id');

        const verifyAndRefresh = async () => {
            setIsRefreshing(true);
            try {
                // If we have a session ID from Stripe, verify the checkout
                // This ensures the subscription is updated even if webhook is delayed
                if (sessionId) {
                    console.log('Verifying checkout session:', sessionId);
                    try {
                        const result = await verifyCheckout(sessionId);
                        console.log('Checkout verification result:', result);
                    } catch (verifyErr) {
                        // Log but don't fail - webhook might still process it
                        console.warn('Checkout verification failed (webhook may handle it):', verifyErr);
                    }
                }

                // Always refresh subscription status to get latest data
                await refreshSubscriptionStatus();
            } catch (err) {
                console.error('Error refreshing subscription:', err);
            } finally {
                setIsRefreshing(false);
            }
        };

        verifyAndRefresh();
    }, [location, refreshSubscriptionStatus, verifyCheckout]);

    return (
        <div className="subscription-result-container">
            <div className="result-card success">
                <div className="icon">✔</div>

                <h1>{t('successPage.title')}</h1>

                <p className="message">
                    {t('successPage.message')}
                </p>

                {isRefreshing ? (
                    <LoadingState />
                ) : (
                    <>
                        <SubscriptionDetails
                            subscriptionData={subscriptionData}
                            formatDate={formatDate}
                        />

                        <UnlockedFeatures />

                        <p className="redirect-info">
                            {t('successPage.redirectInfo', { seconds: countdown })}
                        </p>

                        <div className="actions">
                            <button
                                className="primary-btn"
                                onClick={() => navigate('/dashboard')}
                            >
                                {t('successPage.goToDashboard')}
                            </button>
                            <button
                                className="secondary-btn"
                                onClick={() => navigate('/subscription/plans')}
                            >
                                {t('successPage.manageSubscription')}
                            </button>
                        </div>

                        <p className="mobile-app-note">
                            {t('successPage.mobileAppNote')}
                        </p>
                    </>
                )}
            </div>
        </div>
    );
};

/**
 * Loading state while refreshing subscription
 */
const LoadingState = () => {
    const { t } = useTranslation('subscription');
    return (
        <div className="loading">
            <div className="spinner"></div>
            <p>{t('successPage.refreshing')}</p>
        </div>
    );
};

/**
 * Subscription details section
 */
const SubscriptionDetails = ({ subscriptionData, formatDate }) => {
    const { t } = useTranslation('subscription');
    if (!subscriptionData) return null;

    return (
        <div className="details">
            <h3>{t('successPage.detailsTitle')}</h3>
            <div className="detail-row">
                <span>{t('expired.status')}</span>
                <strong>
                    {subscriptionData.status === 'ACTIVE' ? t('successPage.activeStatus') : subscriptionData.status}
                </strong>
            </div>
            {subscriptionData.endDate && (
                <div className="detail-row">
                    <span>{t('successPage.validUntil')}</span>
                    <strong>{formatDate(subscriptionData.endDate)}</strong>
                </div>
            )}
        </div>
    );
};

/**
 * Unlocked features section
 */
const UnlockedFeatures = () => {
    const { t } = useTranslation('subscription');
    const features = t('successPage.features', { returnObjects: true });
    return (
        <div className="features-activated">
            <h3>{t('successPage.unlockedTitle')}</h3>
            <div className="features-list">
                {Array.isArray(features) && features.map((feature, index) => (
                    <span key={index}>{feature}</span>
                ))}
            </div>
        </div>
    );
};

export default SubscriptionSuccess;
